/**
 * Powers the bundle price cards (variant-picker "cards" style). Each card
 * represents buying N units of the current product with one independent
 * color dropdown per unit — no bundle product, no product-option-count
 * limit. Adds one real single-unit cart line per chosen color (quantities
 * of the same color merged into one line) via the product form's batch
 * addMultipleToCart method. The bundle price shown on the card is purely
 * informational — an automatic Shopify discount (configured in Settings →
 * Discounts for "buy N of this product") brings the cart total down to
 * that price at checkout, so pricing stays correct everywhere without any
 * custom price math.
 *
 * The add-to-cart button is switched from type="submit" to type="button"
 * here (see #takeOverAddToCartButtons). The theme's own form-submit
 * handling is registered as a document-level, capture-phase "submit"
 * listener (see assets/component.js), and since that core script loads
 * before this lazily-loaded one, it always wins a same-phase listener
 * race and adds the form's default single variant before this module gets
 * a chance to preventDefault it. Removing "submit" as the trigger avoids
 * that race entirely instead of trying to out-time it.
 */

/**
 * @param {Element} fieldset
 * @returns {HTMLInputElement | null}
 */
function getCheckedCardRadio(fieldset) {
  const radio = fieldset.querySelector('input[data-bundle-card-radio]:checked');
  return radio instanceof HTMLInputElement ? radio : null;
}

/**
 * @param {Element} fieldset
 */
function syncCardVisibility(fieldset) {
  const checked = getCheckedCardRadio(fieldset);
  const cards = fieldset.querySelectorAll('.variant-option__card');

  cards.forEach((card) => {
    const radio = card.querySelector('input[data-bundle-card-radio]');
    const isSelected = radio instanceof HTMLInputElement && radio === checked;
    const units = card.querySelector('[data-card-units]');

    if (units) {
      units.hidden = !isSelected;
      units.querySelectorAll('select[data-card-unit-select]').forEach((select) => {
        if (select instanceof HTMLSelectElement) select.disabled = !isSelected;
      });
    }
  });
}

/**
 * @param {Element} fieldset
 * @returns {{id: string, options: string[]}[]}
 */
function getVariants(fieldset) {
  const script = fieldset.querySelector('script[data-bundle-variants]');
  if (!script?.textContent) return [];
  try {
    const variants = JSON.parse(script.textContent);
    return Array.isArray(variants) ? variants : [];
  } catch {
    return [];
  }
}

/**
 * @param {{id: string, options: string[]}[]} variants
 * @param {string} color
 * @returns {string | null}
 */
function resolveVariantIdForColor(variants, color) {
  const match = variants.find((variant) => Array.isArray(variant.options) && variant.options.includes(color));
  return match ? String(match.id) : null;
}

/**
 * @param {Element} fieldset
 * @returns {string[]}
 */
function getSelectedColors(fieldset) {
  const checked = getCheckedCardRadio(fieldset);
  if (!checked) return [];

  const card = checked.closest('.variant-option__card');
  if (!card) return [];

  const selects = card.querySelectorAll('select[data-card-unit-select]');
  /** @type {string[]} */
  const colors = [];
  selects.forEach((select) => {
    if (select instanceof HTMLSelectElement) colors.push(select.value);
  });
  return colors;
}

/**
 * Builds one {variantId, quantity} entry per distinct chosen color, merging
 * units of the same color into a single line.
 * @param {Element} fieldset
 * @returns {{variantId: string, quantity: number}[] | null}
 */
function buildCartItems(fieldset) {
  const variants = getVariants(fieldset);
  const colors = getSelectedColors(fieldset);
  if (colors.length === 0 || variants.length === 0) return null;

  /** @type {Map<string, number>} */
  const quantityByVariantId = new Map();

  for (const color of colors) {
    const variantId = resolveVariantIdForColor(variants, color);
    if (!variantId) return null;
    quantityByVariantId.set(variantId, (quantityByVariantId.get(variantId) ?? 0) + 1);
  }

  return Array.from(quantityByVariantId, ([variantId, quantity]) => ({ variantId, quantity }));
}

/**
 * Finds the add-to-cart button(s) tied to this fieldset's product form,
 * switches them off the native submit path, and wires a click handler
 * that adds the currently selected bundle items via addMultipleToCart.
 * @param {Element} fieldset
 */
function takeOverAddToCartButtons(fieldset) {
  const sectionId = fieldset.getAttribute('data-section-id');
  if (!sectionId) return;

  const productForm = document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
  if (!productForm) return;

  const buttons = productForm.querySelectorAll('button[name="add"]');

  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset.bundleTakeover === 'true') return;
    button.dataset.bundleTakeover = 'true';

    // No longer a submit button, so clicking it can never trigger the
    // theme's own native single-variant form submission.
    button.type = 'button';

    button.addEventListener('click', () => {
      const form = button.closest('form');
      if (form && !form.checkValidity()) return;

      const items = buildCartItems(fieldset);
      if (!items || items.length === 0) return;

      const quantityInput = form?.querySelector('input[name="quantity"]');
      const formQuantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;
      const scaledItems = items.map((item) => ({ ...item, quantity: item.quantity * formQuantity }));

      if (typeof (/** @type {any} */ (productForm).addMultipleToCart) === 'function') {
        /** @type {any} */ (productForm).addMultipleToCart(scaledItems);
      }
    });
  });
}

/**
 * @param {ParentNode} root
 */
function init(root) {
  root.querySelectorAll('[data-bundle-color-picker]').forEach((fieldset) => {
    syncCardVisibility(fieldset);
    takeOverAddToCartButtons(fieldset);
  });
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

  const fieldset = target.closest('[data-bundle-color-picker]');
  if (!fieldset) return;

  syncCardVisibility(fieldset);
});

init(document);

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof ParentNode) init(event.target);
});
